import BaseService from '../../base/base_service';
import OAuth2BackendProxy from './oauth2_backend_proxy'
import OAuth2StateFactory from './states/oauth2_state_factory';
import Event from '../../utils/event';
import {SYSTEM_EVENTS} from '../../events/events';
import InactivityTimeoutSettingsPersister from './inactivity_timeout_settings_persister';
import Token from './tokens/token';
import {REFRESH_TOKEN_REASONS} from './states/oauth2_authorized_state';
import {TokenType} from "../../../../common/const/token_type";

export default class OAuth2Service extends BaseService {

    static CLASS = 'OAuth2Service';

    _initParams = null;

    _currentState = null;
    _inactivityTimeoutSettingsPersister = null;
    _loginFromCompletedRegistration = false;

    init(params) {
        super.init(params);

        this._initParams = params;

        this._inactivityTimeoutSettingsPersister = InactivityTimeoutSettingsPersister.create({
            getServiceFactoryFunc: () => this.serviceFactory,
            defaultAccessTokenLifetime: params.defaultAccessTokenLifeTime,
            accessTokenLifeTimeForRegulation: params.accessTokenLifeTimeForRegulation,
            minAccessTokenLifeTime: params.minAccessTokenLifeTime,
            maxAccessTokenLifeTime: params.maxAccessTokenLifeTime
        });

        this._subscribe();
    }


    injectDependencies(dependencies) {
        super.injectDependencies(dependencies);

        //we initialize here the _currentState because only here this.serviceFactory is available
        if (!this._currentState) {
            this._currentState = this._getOAuth2StateFactory().createInitialState();
        }
    }

    getLastSessionData() {
        return  Token.getLastSessionFromStorage();
    }

    _changeState(newState, params) {
        if (this._currentState.constructor === newState.constructor) {
            // We don't want to fire OAUTH_STATE_CHANGE if isn't really the case
            return;
        }

        this._currentState.dispose();
        this._currentState = newState;
        this._currentState.onInitComplete(false);
        Event.fire(SYSTEM_EVENTS.OAUTH_STATE_CHANGE, {loginType: this._currentState.getLoginType(), ...params});
    }


    getFakeClientIP() {
        return this._initParams.fakeClientIP;
    }

    _getBackendProxy() {
        return OAuth2BackendProxy.create({
            tokenUrl: this._initParams.tokenUrl,
            logoutUrl: this._initParams.logoutUrl,
            fakeClientIP: this._initParams.fakeClientIP,
            serviceFactory: this.serviceFactory,
            inactivityTimeoutSettingsPersister: this._inactivityTimeoutSettingsPersister});
    }

    _getOAuth2StateFactory() {
        return OAuth2StateFactory.create({
            stateChangedCallback: this._changeState.bind(this),
            backendProxy: this._getBackendProxy(),
            serviceFactory: this.serviceFactory,
            inactivityTimeoutSettingsPersister: this._inactivityTimeoutSettingsPersister,
            getSessionTimerUrl: this._initParams.getSessionTimerUrl
        });
    }

    _subscribe() {
        this.onSystemEvent(SYSTEM_EVENTS.BEGIN_LOGIN_AFTER_REGISTRATION, this._onBeginLoginFromRegistration);
        this.onSystemEvent(SYSTEM_EVENTS.LOGIN_COMPLETED_AFTER_REGISTERATION, this._onLoginCompletedAfterRegistration);
        this.onSystemEvent(SYSTEM_EVENTS.LOGIN_FAILED_AFTER_REGISTRATION, this._onLoginFailedAfterRegistration);

        this.onSystemEvent(SYSTEM_EVENTS.OAUTH_STATE_CHANGE, this._onOauthChange);
        this.onSystemEvent(SYSTEM_EVENTS.ON_INIT_COMPLETE, this._onInitCompleteHandler);
    }

    _isInAutologinFromRegistration = false;
    _onBeginLoginFromRegistration = () => {
        this._isInAutologinFromRegistration = true;
        this._waitForAutoLoginPromise = new Promise(resolve => {
            this._waitForAutoLoginResolver = resolve;
        });
    }

    _onLoginCompletedAfterRegistration = () => {
        this._loginFromCompletedRegistration = true;
        this._isInAutologinFromRegistration = false;
        this._waitForAutoLoginResolver();
    }

    _onLoginFailedAfterRegistration = () => {
        this._isInAutologinFromRegistration = false;
        this._waitForAutoLoginResolver();
    }

    _onOauthChange() {
        if(!this.isAuthorized()) {
            this._loginFromCompletedRegistration = false;
        }
    }

    isLoginFromRegistration() {
        return this._loginFromCompletedRegistration;
    }

    _onInitCompleteHandler() {
        this._currentState.onInitComplete(true);
    }

    isAuthorized() {
        return this._currentState.isAuthorized();
    }

    tryToRememberMe() {
        return this._currentState.tryToRememberMe();
    }

    login(credentials) {
        return this._currentState.login(credentials);
    }

    isInAutologin() {
        return this._isInAutologinFromRegistration || this.serviceFactory.queryStringDetectorService().isInAutologin();
    }

    _waitForAutoLoginResolver = () => {};
    _waitForAutoLoginPromise = null;
    waitForAutologin() {
        if(this.isInAutologin()) {
            if(!this._waitForAutoLoginPromise) {
                this._waitForAutoLoginPromise = new Promise((resolve) => {
                    this._waitForAutoLoginResolver = resolve;
                });
            }
            return this._waitForAutoLoginPromise

        } else {
            return Promise.resolve();
        }

    }



    runAutoLogin(token, sessionTimerOffset, sourceProductPackage, brandId, sourceSubBrandId, targetSubBrandId, tokenType, businessCorrelationId) {

        if(tokenType === TokenType.MigrationToken) {
            this.serviceFactory.spinnerProxyService().startSpinner(true);
        }

        const resolveWaitForAutologin = () => {
            this._waitForAutoLoginResolver();
            this._waitForAutoLoginPromise = null;
            this._waitForAutoLoginResolver = () => {};
        }

        const autologinWithTokenFunc = () => {
            return this._currentState.autologinWithToken(token, sessionTimerOffset, sourceProductPackage, brandId, sourceSubBrandId, targetSubBrandId, tokenType, businessCorrelationId)
                .then(() => {
                    if (this.isAuthorized()) {
                        Event.fire(SYSTEM_EVENTS.AUTOLOGIN_SUCCESS);
                    } else {
                        Event.fire(SYSTEM_EVENTS.AUTOLOGIN_FAILED);
                    }
                    resolveWaitForAutologin();

                }).catch(() => {
                    Event.fire(SYSTEM_EVENTS.AUTOLOGIN_FAILED);
                    resolveWaitForAutologin();
                });
        };

        const executeLogout = () => {
            if(this._initParams.executeClientSideLogoutBeforeAutologin) {
                return this._currentState.clientSideLogout();
            } else {
                return this._currentState.logout();
            }

        }

        if (token) {
            if (this.isAuthorized()){
                return executeLogout().then(() => {
                  return autologinWithTokenFunc();
                }).catch(() => {
                    return autologinWithTokenFunc();
                });
            } else {
              return autologinWithTokenFunc();
            }
        } else {
            resolveWaitForAutologin();
            return Promise.reject('Invalid token null or undefined');
        }
    }

    /**
     *
     * @param options: {shouldShowLogoutMessage?: boolean}
     * @returns {*}
     */
    logout(options = {}) {
        options = {
            shouldShowLogoutMessage: true,
            ...options
        }
        return this._currentState.logout().then(()=>{
            if(options.shouldShowLogoutMessage) {
                this.showLogoutMessage();
            }
        });
    }

    showLogoutMessage() {
        this.serviceFactory.regulationService().getRegulation().showLogoutMessage();
    }

    getAutologinKey() {
        return this._initParams.autologinKey;
    }

    getToken() {
        return this._currentState.getDecodedAccessToken();
    }

    getTokenAuthorizeHeader() {
        return this._currentState.getTokenAuthorizeHeader();
    }

    extractTokenProperty(tokenProperty) {
        return this._currentState.extractTokenProperty(tokenProperty);
    }

    isVIP() {
        return this._currentState.isVIP();
    }

    getUserData() {
        return this._currentState.getUserData();
    }

    getAccountData() {
        return this._currentState.getAccountData();
    }

    updateTokenData(filedName, fieldValue) {
        this._currentState.updateTokenData(filedName, fieldValue);
    }

    getRefreshToken() {
        return this._currentState.getRefreshToken();
    }

    /**
     *
     * @param timeSpan
     * @returns {Promise}
     */
    setCustomSessionTimeout(timeSpan) {
        this._inactivityTimeoutSettingsPersister.setCustomAccessTokenLifeTime(timeSpan);
        this.refreshToken(REFRESH_TOKEN_REASONS.user_set_custom_session);
    }

    /**
     *
     * @returns {TimeSpan}
     */
    getCurrentSessionTimeoutSettings() {
        return {
            currentSessionTimeout: this._inactivityTimeoutSettingsPersister.getAccessTokenLifeTime(),
            minSessionTimeout: this._inactivityTimeoutSettingsPersister.getMinAccessTokenLifeTime(),
            maxSessionTimeout: this._inactivityTimeoutSettingsPersister.getMaxAccessTokenLifeTime(),
            possibleSessionTimeoutValues: this._inactivityTimeoutSettingsPersister.getPossibleAccessTokenLifeTimeValues()
        };
    }


    refreshToken(reason) {
        return this._currentState.refreshToken(reason);
    }

    clearTokenInfoFromLocalStorage() {
        Token.fromStorage().delete();
    }

    /**
     *
     * @returns {Promise.<TimeSpan>}
     */
    getTimeSinceLogin() {
        return this._currentState.getTimeSinceLogin();
    }

    /**
     *
     * @returns {number}
     * 1 - NewLogin (Manual Login)
     * 3 - Autologin
     */
    getLoginType() {
        return 1 * this._currentState.getLoginType();
    }

    /**
     *
     * @returns {number}
     */
    getSourceProductPackage() {
        return 1 * this._currentState.getSourceProductPackage();
    }

    onInitializationCompleted() {
        return this.tryToRememberMe().catch((error) => {
            this.logger.debug('Remember me check failed onInitializationCompleted', error);
        });
    }
}